<script lang="ts">
import { defineComponent, Ref, ref, toRaw } from 'vue';
import LocationAPI from 'src/core/api/LocationAPI';
import { type AddressEditInfo, AddressesStore, type AddressFormItem } from 'stores/Addresses/Addresses.ts';
import type { AddressItem } from 'src/core/types/Addresses';
import { useQuasar } from 'quasar';

const locationApi = new LocationAPI();

export default defineComponent({
  name: 'AddAddress',

  props: {
    modelValue: {
      type: Boolean,
      default: false,
      required: true,
    },
    editDetail: {
      type: Object,
      required: false,
    },
  },

  setup() {
    const addressesStore = AddressesStore();
    const $q = useQuasar();

    const provincesList = ref([]);
    const districts = ref([]);
    const districtsList = ref([]);

    const addressInfo: Ref<AddressFormItem> | Ref<AddressEditInfo> = ref({
      name: '',
      nameSurname: '',
      country: 'Türkiye',
      city: '',
      district: '',
      neighbourhood: '',
      address: '',
      postcode: '',
      telephone: '',
      type: '1',
      citizenShipNumber: '',
      companyName: '',
      taxNumber: '',
      taxOffice: '',
      defaultAddress: false,
    });
    const addressActionLoading = ref(false);

    const cities = JSON.parse(JSON.stringify(toRaw(locationApi.getCities())));
    return {
      addressesStore,
      $q,
      addressInfo,
      addressActionLoading,
      cities,
      provincesList,
      districts,
      districtsList,
      filterProvincesFn(val: string, update: (updateFn: () => void) => void) {
        if (val === '') {
          update(() => {
            provincesList.value = cities;
          });
          return;
        }

        update(() => {
          const needle = val.toLowerCase();
          provincesList.value = cities.filter((v) => v.name.toLowerCase().indexOf(needle) > -1);
        });
      },
      filterDistrictsFn(val: string, update: (updateFn: () => void) => void) {
        if (val === '') {
          update(() => {
            districtsList.value = districts.value;
          });
          return;
        }

        update(() => {
          const needle = val.toLowerCase();
          districtsList.value = districts.value.filter((v) => v.name.toLowerCase().indexOf(needle) > -1);
        });
      },
    };
  },

  methods: {
    addAddress() {
      const addressInfo = this.addressInfo;
      this.addressActionLoading = true;
      this.addressesStore
        .addNewAddress(addressInfo)
        .then(({ status, message, data }) => {
          if (status._200_) {
            this.$q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              message: 'Hesabınıza yeni bir adres eklediniz',
            });
            this.resetAddressForm();
            this.closeDialog();
            this.$emit('update', data);
            this.$emit('create', data);
          } else {
            this.$q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });
          }
          this.addressActionLoading = false;
        })
        .catch(() => {
          this.addressActionLoading = false;
        });
    },

    editAddress() {
      const addressInfo = this.addressInfo;
      this.addressActionLoading = true;
      this.addressesStore
        .editAddress({ ...addressInfo, addressId: this.editDetail?.id })
        .then(({ status, message, data }) => {
          if (status._200_) {
            this.$q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              message: 'Adres başarılı bir şekilde güncellenmiştir',
            });
            this.resetAddressForm();
            this.closeDialog();
            this.$emit('update', data);
          } else {
            this.$q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });
          }
          this.addressActionLoading = false;
        })
        .catch(() => {
          this.addressActionLoading = false;
        });
    },

    resetAddressForm() {
      this.addressInfo.name = '';
      this.addressInfo.nameSurname = '';
      this.addressInfo.city = '';
      this.addressInfo.district = '';
      this.addressInfo.neighbourhood = '';
      this.addressInfo.address = '';
      this.addressInfo.postcode = '';
      this.addressInfo.telephone = '';
      this.addressInfo.citizenShipNumber = '';
      this.addressInfo.companyName = '';
      this.addressInfo.taxNumber = '';
      this.addressInfo.taxOffice = '';
      this.addressInfo.defaultAddress = false;
    },

    closeDialog() {
      if (this.editDetail) {
        this.resetAddressForm();
      }
      this.$emit('update:modelValue', false);
    },
  },

  watch: {
    'addressInfo.city': {
      handler(val) {
        const [districtDetail] = locationApi.getProvinces(val);
        this.districts = districtDetail.districts;
      },
    },
    editDetail(data: AddressItem) {
      this.addressInfo.name = data.name;
      this.addressInfo.nameSurname = data.namesurname;
      this.addressInfo.city = data.city;
      this.addressInfo.district = data.district;
      this.addressInfo.neighbourhood = data.neighbourhood;
      this.addressInfo.address = data.address;
      this.addressInfo.postcode = String(data.postcode);
      this.addressInfo.telephone = data.telephone;
      this.addressInfo.citizenShipNumber = String(data.citizenshipnumber);
      this.addressInfo.companyName = data.companyname;
      this.addressInfo.taxNumber = data.taxnumber;
      this.addressInfo.taxOffice = data.taxoffice;
    },
  },
});
</script>

<template>
  <div>
    <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" persistent>
      <q-card style="min-width: 450px">
        <q-card-section class="bg-primary text-white q-mb-md">
          <div class="text-h6" v-if="!$slots.adresTitle">{{ !editDetail ? 'Yeni Adres Ekle' : 'Adresi Düzenle' }}</div>
          <slot v-else name="adresTitle"></slot>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <div class="row">
            <div class="col-xs-12 col-md-6 q-pa-sm">
              <div class="q-mb-sm">
                <q-input dense v-model="addressInfo.name" filled label="Adres Başlığı (Opsiyonel)" />
              </div>
              <div class="q-mb-sm">
                <q-input dense v-model="addressInfo.nameSurname" filled label="Adı Soyadı" />
              </div>
              <div class="q-mb-sm">
                <q-select dense label="Ülke" transition-show="flip-up" transition-hide="flip-down" filled v-model="addressInfo.country" disable :options="['Türkiye']" />
              </div>
              <div class="q-mb-sm">
                <q-select
                  dense
                  label="Şehir"
                  transition-show="scale"
                  transition-hide="scale"
                  filled
                  v-model="addressInfo.city"
                  option-value="name"
                  option-label="name"
                  use-input
                  emit-value
                  map-options
                  :options="provincesList"
                  :behavior="$q.platform.is.mobile ? 'dialog' : 'menu'"
                  @filter="filterProvincesFn"
                />
              </div>
              <div class="q-mb-sm">
                <q-select
                  dense
                  label="İlçe"
                  transition-show="scale"
                  transition-hide="scale"
                  filled
                  option-value="name"
                  option-label="name"
                  use-input
                  emit-value
                  map-options
                  v-model="addressInfo.district"
                  :options="districtsList"
                  :disable="!districts.length"
                  :behavior="$q.platform.is.mobile ? 'dialog' : 'menu'"
                  @filter="filterDistrictsFn"
                />
              </div>
              <div class="q-mb-sm">
                <q-input dense label="Mahalle / Köy" v-model="addressInfo.neighbourhood" filled />
              </div>
              <q-input dense label="Adres" v-model="addressInfo.address" filled type="textarea" />
            </div>
            <div class="col-xs-12 col-md-6 q-pa-sm">
              <div class="q-mb-sm">
                <q-input dense filled v-model="addressInfo.postcode" label="Posta Kodu" />
              </div>
              <div class="q-mb-sm">
                <q-input dense filled v-model="addressInfo.telephone" label="Cep Telefonu" placeholder="(555) 000 00 00" mask="(###) ### ## ##" />
              </div>
              <div class="q-mb-sm">
                <q-radio v-model="addressInfo.type" val="1" size="sm" label="Bireysel" />
                <q-radio v-model="addressInfo.type" val="2" size="sm" label="Kurumsal" />
              </div>
              <div v-if="addressInfo.type === '1'">
                <div class="q-mb-sm">
                  <q-input dense filled maxlength="11" label="T.C. No" mask="###########" v-model="addressInfo.citizenShipNumber" />
                </div>
              </div>
              <div v-else>
                <div class="q-mb-sm">
                  <q-input filled dense label="Firma Adı" v-model="addressInfo.companyName" />
                </div>
                <div class="q-mb-sm">
                  <q-input filled dense label="Vergi Numarası" v-model="addressInfo.taxNumber" />
                </div>
                <div class="q-mb-sm">
                  <q-input filled dense label="Vergi Dairesi" v-model="addressInfo.taxOffice" />
                </div>
              </div>
              <div v-if="!editDetail">
                <q-toggle v-model="addressInfo.defaultAddress"> Bu adresi varsayılan yap</q-toggle>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="text-primary q-pa-md">
          <div v-if="!$slots.adresButton">
            <q-btn color="red-8" class="q-mr-sm text-weight-bold" label="İptal" @click="closeDialog" no-caps />
            <q-btn v-if="!editDetail" color="primary" @click="addAddress" class="text-weight-bold" no-caps :loading="addressActionLoading">
              <span class="q-px-xs">
                <q-icon name="fa-solid fa-circle-plus" size="1rem" class="q-mr-sm" />
                Adresi Ekle
              </span>
            </q-btn>
            <q-btn v-else color="green-6" @click="editAddress" class="text-weight-bold" no-caps :loading="addressActionLoading">
              <span class="q-px-xs">
                <q-icon name="fa-solid fa-check" size="1rem" class="q-mr-sm" />
                Adresi Güncelle
              </span>
            </q-btn>
          </div>
          <div v-else>
            <slot name="adresButton"></slot>
          </div>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style scoped></style>
